import { useEffect, useCallback, useRef } from "react";

import { BatchInvite } from "../../../models/invitations";

import { useInvalidateMembers, useMaybeMembersData } from "../members";

import { useMaybeInvitesData } from "./invites";
import { useInvalidateInvites } from "./invites.invalidate";
import type { PayloadOfUseInviteMembers } from "./invites.mutations";

// FIXME: This is a band-aid over the bad practice of server-side returning "success" from a call before it has actually been processed.
// We are kicking off invalidation requests after arbitrary amounts of time (with exponential backoff) in the hope that backend has processed this data by those points. This is a waste of resources and needs to be fixed server-side.
export const usePollingInvalidationsForUseInviteMembers = () => {
  const invalidateInvites = useInvalidateInvites();
  const invalidateMembers = useInvalidateMembers();

  const maybeMembersData = useMaybeMembersData();
  const maybeInvitesData = useMaybeInvitesData();

  const membersDataRef = useRef(maybeMembersData);
  const invitesDataRef = useRef(maybeInvitesData);

  useEffect(() => {
    membersDataRef.current = maybeMembersData;
  }, [maybeMembersData]);
  useEffect(() => {
    invitesDataRef.current = maybeInvitesData;
  }, [maybeInvitesData]);

  return useCallback(
    ({
      payload,
      failedInvites,
      maxIterations = 10,
      backoffFactor = 1.5,
      initialIterationMs = 500,
    }: {
      payload: PayloadOfUseInviteMembers;
      failedInvites: BatchInvite[];
      maxIterations?: number;
      backoffFactor?: number;
      initialIterationMs?: number;
    }) => {
      const invitesToLookFor: BatchInvite[] = payload.invites.filter(
        (invite) => {
          for (const failedInvite of failedInvites) {
            if (
              failedInvite.phoneOrEmail === invite.phoneOrEmail &&
              failedInvite.role === invite.role
            ) {
              return false;
            }
          }
          return true;
        },
      );

      const MAX_ITERATIONS = maxIterations;
      const BACKOFF_FACTOR = backoffFactor;
      let iterationsCompletedCount = 0;
      let nextIterationTimeoutMs = initialIterationMs;
      const doInvalidations = async () => {
        const totalInvites = invitesToLookFor.length;
        const invitesFound: BatchInvite[] = [];
        if (membersDataRef.current) {
          for (const invite of invitesToLookFor) {
            for (const member of membersDataRef.current.members) {
              const matchesEmailOrPhone =
                (member.email && member.email === invite.phoneOrEmail) ||
                (member.phone && member.phone === invite.phoneOrEmail);
              const matchesRole =
                (invite.role === "administrator" &&
                  member.permissions.isAdmin) ||
                (invite.role === "advisor" && member.permissions.isAdvisor) ||
                (invite.role === "editor" && member.permissions.isEditor) ||
                (invite.role === "viewer" && member.permissions.isViewer);
              if (matchesEmailOrPhone && matchesRole) {
                console.debug(
                  `[@fabric/core][useInviteMembers] doInvalidations found ${invite.phoneOrEmail} in membersData`,
                  {
                    invite,
                    invitesDataRef,
                  },
                );
                invitesFound.push(invite);
              }
            }
          }
        }
        if (invitesDataRef.current) {
          for (const invite of invitesToLookFor) {
            for (const pendingInvite of invitesDataRef.current
              .pendingInvitations) {
              const matchesEmailOrPhone =
                pendingInvite.email &&
                pendingInvite.email === invite.phoneOrEmail;
              const matchesRole =
                (invite.role === "administrator" &&
                  pendingInvite.permissions.isAdmin) ||
                (invite.role === "advisor" &&
                  pendingInvite.permissions.isAdvisor) ||
                (invite.role === "editor" &&
                  pendingInvite.permissions.isEditor) ||
                (invite.role === "viewer" &&
                  pendingInvite.permissions.isViewer);
              if (matchesEmailOrPhone && matchesRole) {
                console.debug(
                  `[@fabric/core][useInviteMembers] doInvalidations found ${invite.phoneOrEmail} in invitesData`,
                  {
                    invite,
                    invitesDataRef,
                  },
                );
                invitesFound.push(invite);
              }
            }
          }
        }

        console.debug("[@fabric/core][useInviteMembers] doInvalidations", {
          iterationsCompletedCount,
          totalInvites,
          invitesToLookFor,
          invitesFound,
          membersDataRef,
          invitesDataRef,
        });

        if (
          invitesFound.length < totalInvites &&
          iterationsCompletedCount < MAX_ITERATIONS
        ) {
          Promise.all([invalidateInvites(), invalidateMembers()]);
          window.setTimeout(doInvalidations, nextIterationTimeoutMs);
        }

        nextIterationTimeoutMs *= BACKOFF_FACTOR;
        iterationsCompletedCount += 1;
      };
      doInvalidations();
    },
    [invalidateInvites, invalidateMembers],
  );
};
